import React, { PureComponent } from 'react'
import { select, scaleTime, scaleLinear, axisBottom, easeLinear, drag, event as d3event } from 'd3'
import DateService from 'live/services/date'
import pointService from 'live/services/charts/point'
import localeService from 'live/services/locale'
import theme from 'live/services/theme'

import TimelineToolTip from './timeline-tooltip'
import Annotation from '../../annotations/annotation'

import _ from 'lodash'
import timelineService from '../../services/timeline'
import moment, { Moment } from 'moment'

const currentUser = Live.currentUser

export const isAdmin = () => Live.Permission.ADMIN || Live.Permission.SUPER_USER
export const canManage = () => isAdmin() || Live.Permission.EDIT_ANNOTATIONS

export const ANNOTATIONS_URL = '/services/plugin-annotations/annotations'
const CURSOR_WIDTH = 2
type TimelineStore = {
    timelineConfig: {
        disabled: boolean
        mode: string
        applyDashboardFiltersToTimeline: boolean
    }
}

type Props = {
    span: string
    dashboardId: number
    containerWidth: number
    updateSpan: Function
    activeDashboardFilters: Record<string, Array<string>>
    isVisible: boolean
} & TimelineStore

type State = {
    eventsHovered: Array<Record<string, any>>
    loading: boolean
    showForm: boolean
    showTooltip: boolean
    showConfirmation: boolean
    tooltip: Record<string, any>
}

type FakeMouseEvent = {
    overrideX: number
    hideTooltip?: boolean
    togglingTooltip?: boolean
}

const TIMELINE_MARGIN = 20
const TIMELINE_MARGINS = 2 * TIMELINE_MARGIN

export default class Timeline extends PureComponent<Props, State> {
    INSTANT_EVENT_WIDTH = 20

    unmounting: boolean
    cursorShowing: boolean
    pointSub = pointService.currentPoint$.subscribe(point => {
        if (point.source !== 'timeline') {
            this.onPointHovered(point)
        }
    })

    d3: Record<string, any>
    historyQuerySpan: boolean
    historyQueryStarted: boolean
    selections: Record<string, any>
    tooltipRef: React.RefObject<HTMLDivElement>
    tooltipCrosshairRef: React.RefObject<HTMLDivElement>
    mouseCurrentX: number
    moveTooltip: (e: MouseEvent | FakeMouseEvent) => void

    constructor(props: Props) {
        super(props)

        this.state = {
            eventsHovered: [],
            loading: true, // loading goes false when onData with a span is called
            showConfirmation: false,
            showForm: false,
            showTooltip: false,
            tooltip: {}
        }

        this.throttleFunctions()
        this.initD3()
        this.selections = {}
        this.historyQueryStarted = false
        this.historyQuerySpan = false
        this.moveTooltip = moveTooltip.bind(this)
    }

    handleClickOutside = (e): void => {
        if (!this.tooltipComponent.state.Form) {
            this.onClose()
        }
    }

    componentDidUpdate(prevProps): void {
        const newWidth = this.timelineWidth()

        if (newWidth !== this.d3.dimensions.width) {
            this.d3.dimensions.width = newWidth
            this.redraw(false)
        }
        if (prevProps.timelineConfig.mode !== this.props.timelineConfig.mode) {
            this.redraw(false)
        }
    }

    componentDidMount(): void {
        this.d3.selections.svg = select(this.refs.svg).append('g')
        this.d3.selections.axisContainer = this.d3.selections.svg.append('g').attr('transform', 'translate(0, 1)')
        this.d3.selections.cursor = select(this.refs.cursor)
        this.selections.tooltipValue = this.tooltipRef && this.tooltipRef.querySelector('#timeline__tooltip-value')
        this.selections.tooltipNotes = this.tooltipRef && this.tooltipRef.querySelector('#timeline__tooltip-notesQty')

        const { span, dashboardId } = this.props

        this.addDOMEventListeners()
        timelineService.setupTimelineRequest(
            span,
            dashboardId,
            this.onData,
            this.props.timelineConfig.applyDashboardFiltersToTimeline ? this.props.activeDashboardFilters : undefined
        )
    }

    componentWillUnmount(): void {
        this.unmounting = true
        timelineService.closeTimelineRequest()
        this.removeEventListeners()
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props): void {
        const { span, dashboardId, isVisible, activeDashboardFilters, timelineConfig } = nextProps

        if (this.props.isVisible !== isVisible) {
            if (this.refs.container) this.refs.container.style.height = isVisible ? '20px' : 0

            // waiting timeline animation
            setTimeout(this.props.resizeDashboardLayout, 250)
        }

        const newSpan = this.props.span !== span
        const newFilters = !_.isEqual(this.props.activeDashboardFilters, activeDashboardFilters)
        const applyFiltersChanged =
            this.props.timelineConfig.applyDashboardFiltersToTimeline !== timelineConfig.applyDashboardFiltersToTimeline

        if ((newSpan || newFilters || applyFiltersChanged) && !this.unmounting) {
            this.clearTimelineData()
            timelineService.setupTimelineRequest(
                span,
                dashboardId,
                this.onData,
                timelineConfig.applyDashboardFiltersToTimeline ? activeDashboardFilters : undefined
            )
        }
    }

    onPointHovered({ x }): void {
        if (x !== null) {
            if (typeof this.d3.scale === 'function') {
                const position = this.d3.scale(x)
                if (!this.cursorShowing) {
                    this.refs.cursor.style.fill = theme['brand-primary']
                    this.cursorShowing = true
                }

                this.moveTooltip({
                    overrideX: position,
                    hideTooltip: true
                })
            }
        } else {
            // do not change the tooltip when the menu is open
            if (this.state.showTooltip || this.state.showForm) return

            this.refs.cursor.style.fill = 'transparent'
            this.cursorShowing = false
        }
    }

    mouseEnter = (e: MouseEvent): void => {
        this.tooltipRef.style.opacity = 1
        this.refs.cursor.style.fill = theme['brand-primary']
        this.cursorShowing = true
        this.moveTooltip(e)
    }

    onMouseMove = (e: MouseEvent) => {
        this.moveTooltip(e)

        // updating global pointService value
        const date = this.d3.scale?.invert?.(this.mouseCurrentX)
        if (date != null) {
            // convers date to int, point expect a unix timestamp
            pointService.currentPoint$.next({ x: +date, source: 'timeline' })
        }
    }

    addDOMEventListeners = (): void => {
        const selectSpanDisabled = this.props.selectSpanDisabled

        // drag
        select(this.refs.svg).call(
            drag()
                .on('start', () => {
                    const x = d3event.x

                    this.d3.selections.drag = this.d3.selections.svg
                        .append('rect')
                        .attr('class', 'drag-cursor')
                        .attr('width', 0)
                        .attr('fill-opacity', '0.1')
                        // .attr('fill', '')
                        .attr('height', 35)
                        .attr('x', x)

                    this.d3.selections.drag.initialX = x
                })
                .on('drag', () => {
                    const drag = this.d3.selections.drag

                    if (selectSpanDisabled) {
                        drag.remove()

                        return
                    }

                    const initialX = drag.initialX
                    const ex = d3event.x

                    drag.attr('x', ex <= initialX ? ex : initialX).attr('width', Math.abs(initialX - ex))

                    this.moveTooltip({ overrideX: d3event.x })
                })
                .on('end', () => {
                    const drag = this.d3.selections.drag
                    const initialX = drag.initialX
                    const endX = d3event.x

                    // Timeline click to open Tooltip
                    if (+initialX === +endX) {
                        drag.remove()

                        const showForm = !this.state.eventsHovered.length

                        if (showForm && !(canManage() || isAdmin())) return

                        this.toggleTooltip(showForm)

                        return
                    } else if (selectSpanDisabled) {
                        drag.remove()

                        return
                    }

                    let start = this.d3.scale.invert(initialX)
                    let end = this.d3.scale.invert(endX)
                    let tmp

                    if (this.props.timelineConfig.mode === 'temporal') {
                        start = start.getTime()
                        end = end.getTime()
                    }

                    if (start > end) {
                        tmp = start
                        start = end
                        end = tmp
                    }

                    if (end - start > 1000) {
                        this.props.updateSpan(
                            'apply',
                            {
                                start,
                                end
                            },
                            'timeline'
                        )
                    }

                    drag.remove()
                })
        )

        // moves tooltip
        if (this.refs.svg) {
            this.refs.svg.addEventListener('mousemove', this.onMouseMove)
            this.refs.svg.addEventListener('mouseenter', this.mouseEnter)
        }

        this.mouseLeave = mouseLeave.bind(this)

        // hides tooltip
        this.refs.content && this.refs.content.addEventListener('mouseleave', this.mouseLeave)

        window.addEventListener('keydown', this.onKeyDown)
    }

    onKeyDown = (e: React.KeyboardEvent): void => {
        if (e.keyCode === 27) this.hideTooltip()
    }

    removeEventListeners = (): void => {
        if (this.refs.svg) {
            this.refs.svg.removeEventListener('mousemove', this.onMouseMove)
            this.refs.svg.removeEventListener('mouseenter', this.mouseEnter)
        }
        this.pointSub.unsubscribe()
        this.refs.content && this.refs.content.removeEventListener('mouseleave', this.mouseLeave)
        window.removeEventListener('keydown', this.onKeyDown)
    }

    clearTimelineData = (): void => {
        this.d3.data = []
        this.d3.ranges = [] // pixel ranges used on events (used to calculate hover effect)
    }

    initD3 = (): void => {
        this.d3 = {
            selections: {},
            data: [],
            ranges: [], // pixel ranges used on events (used to calculate hover effect)
            dimensions: {}
        }

        this.d3.dimensions.width = this.timelineWidth()
        this.d3.dimensions.height = 35
    }

    deleteD3Events = (predicate: (event: any) => boolean): void => {
        this.d3.data = this.d3.data.filter(predicate)
        this.d3.ranges = this.d3.ranges.filter(d => predicate(d.event))
    }

    onData = (type: string, e: Record<string, any>): void => {
        if (this.unmounting) return

        const events = []
        let lastSpan
        let forceRedraw = false
        if (!this.historyQueryStarted && type === 'start') {
            this.d3.data = []
            this.d3.ranges = []
            this.historyQueryStarted = true
        }

        if (type === 'span') {
            this.historyQuerySpan = e.begin !== e.end

            if (this.historyQuerySpan) lastSpan = e
        }

        if (type === 'event') {
            const evToDelete = e.filter(event => event.__delete)[0]
            if (evToDelete) {
                // A deleted event arrives in real time after its deletion
                // Take the deleted event off of the buffer to do NOT redraw it
                const uidToDelete = evToDelete.uid

                e = e.filter(event => event.uid !== uidToDelete)
                this.deleteD3Events(d => d.uid !== uidToDelete)
                forceRedraw = true
            }

            events.push(...e)
        }

        let eidx

        // updates d3 data
        events.map(e => {
            eidx = _.findIndex(this.d3.data, d => d.uid === e.uid)

            if (eidx === -1) this.d3.data.push(e)
            else this.d3.data[eidx] = e
        })

        if (lastSpan) {
            this.d3.span = lastSpan
            this.deleteD3Events(e => (e.end || e.begin) > lastSpan.begin)
            if (this.state.loading) this.setState({ loading: false })
        }
        if (lastSpan || events.length || forceRedraw) this.redraw(true)
    }

    /**
     * Creates a d3 scale.
     *
     * @returns {any} a d3 linearScale or timeScale
     *
     * @memberof Timeline
     * @param begin
     * @param end
     */
    createScale = (begin = 0, end = 0): void => {
        const { mode } = this.props.timelineConfig

        const scale = mode === 'temporal' ? scaleTime : scaleLinear

        if (mode === 'temporal') {
            begin = new Date(begin)
            end = new Date(end)
        }

        return scale().domain([begin, end]).range([0, this.d3.dimensions.width]).clamp(true)
    }

    updateD3Scale = (): void => {
        const { mode } = this.props.timelineConfig

        const begin = this.d3.span && this.d3.span.begin
        const end = this.d3.span && this.d3.span.end

        this.d3.scale = this.createScale(begin, end)

        this.d3.axis = axisBottom(this.d3.scale).ticks(6).tickSizeOuter(0)
        if (mode === 'temporal') this.d3.axis.tickFormat(localeService.d3TickFormat)

        this.d3.selections.axisContainer.call(this.d3.axis)
    }

    /**
     * Redraws the timeline.
     *
     * @param {bool} animate Whether to animate or not the d3's update selection.
     *
     * @memberof Timeline
     */
    redraw = (animate: boolean): void => {
        this.updateD3Scale()

        const tooltip = this.state.tooltip

        if (tooltip && tooltip.value) {
            let tooltipValue = tooltip.value

            if (moment.isMoment(tooltipValue)) tooltipValue = tooltipValue._d

            const newPos = this.d3.scale(tooltipValue)

            this.d3.selections.cursor.attr('x', () => newPos)
        }

        const content = this.d3.selections.svg.selectAll('rect.event').data(this.d3.data, d => d.uid)
        // resize & reposition
        let update = content.attr('x', this.getEventX)

        if (animate) update = update.transition().duration(500)

        update.attr('width', this.getEventWidth).attr('fill', d => d.__color || theme['brand-primary'])

        // create & position
        content
            .enter()
            .append('rect')
            .attr('width', this.getEventWidth)
            .attr('height', 10)
            .attr('fill', d => d.__color || theme['brand-primary'])
            .attr('fill-opacity', '0.7')
            .attr('x', this.getEventX)
            .attr('class', 'event')

        // exit
        content.exit().transition(easeLinear).duration(250).style('opacity', '0').remove()
    }

    toggleTooltip = (showForm: boolean) => {
        const showTooltip = this.state.showTooltip
        const newShowTooltip = !showTooltip

        this.setState(
            {
                showTooltip: newShowTooltip,
                showForm: showForm && newShowTooltip
            },
            this.updateCursorAndTooltipPos
        )
    }

    updateCursorAndTooltipPos = (): void => {
        this.moveTooltip({ overrideX: this.mouseCurrentX, togglingTooltip: true })
    }

    hideTooltip = (): void => {
        if (this.unmounting) return

        this.setState(
            {
                showTooltip: false,
                showForm: false
            },
            this.updateCursorAndTooltipPos
        )
    }

    /**
     * Get event x position.
     *
     * @param {any} e The event.
     *
     * @returns {Number} The x position of the event.
     *
     * @memberof Timeline
     */
    getEventX = (e: any) => {
        let begin = e.begin || e.timestamp
        if (this.props.timelineConfig.mode === 'temporal') begin = new Date(begin)

        return this.d3.scale(begin)
    }

    getEventWidth = (event, i, elements): number => {
        const range = { begin: event.begin || event.timestamp, end: event.end }

        // instant event
        if (!range.end) {
            const begin = this.d3.scale(range.begin)

            this.d3.ranges[i] = {
                begin,
                end: begin + this.INSTANT_EVENT_WIDTH,
                element: elements[i],
                event
            }

            return this.INSTANT_EVENT_WIDTH
        }

        if (this.props.timelineConfig.mode === 'temporal') {
            range.begin = new Date(range.begin)
            range.end = new Date(range.end)
        }

        const scaleBegin = this.d3.scale(range.begin)
        let scaleEnd = this.d3.scale(range.end)
        let wid = scaleEnd - scaleBegin

        // sets the min width
        if (scaleEnd - scaleBegin < this.INSTANT_EVENT_WIDTH) {
            scaleEnd += this.INSTANT_EVENT_WIDTH - wid
        }

        wid = scaleEnd - scaleBegin

        const eventRange = {
            begin: scaleBegin,
            end: scaleEnd,
            element: elements[i],
            event
        }

        this.d3.ranges[i] = eventRange

        return wid
    }

    onClose = (): void => {
        if (this.unmounting) return

        this.tooltipRef.style.opacity = 0
        this.refs.cursor.style.fill = 'transparent'
        this.cursorShowing = false

        setTimeout(() => {
            this.hideTooltip()
        }, 250) // wait for the animation to finish
    }

    onDeleteEvent = (): void => {
        // the DELETE post sends an event that is caught on the 'onData' method
        this.onClose()
        this.redraw(false)
    }

    setTooltipRef = (ref: React.RefObject<HTMLDivElement>): void => {
        this.tooltipRef = ref
    }

    setTooltipCrosshairRef = (ref: React.RefObject<HTMLDivElement>): void => {
        this.tooltipCrosshairRef = ref
    }

    timelineWidth = (): number =>
        this.props.containerWidth >= TIMELINE_MARGINS ? this.props.containerWidth - TIMELINE_MARGINS : 0 // 20px for each margin defined in the css file

    render(): React.ReactElement {
        const { dashboardId, timelineConfig, isVisible, activeDashboardFilters } = this.props
        const { showTooltip, showForm, eventsHovered, tooltip = {} } = this.state
        const { mode } = timelineConfig
        const dimensions = { width: `${this.timelineWidth()}px`, height: '20px' }

        return (
            <div ref="container" className={`timeline ${isVisible ? '' : 'timeline--hidden'}`}>
                <div ref="content" className="timeline__content">
                    <svg ref="svg" className="timeline__root-svg" {...dimensions}>
                        <rect
                            ref="cursor"
                            className="cursor"
                            width={CURSOR_WIDTH}
                            height="9"
                            fill="transparent"
                            y="0"
                            x="500"
                        />
                    </svg>

                    <TimelineToolTip
                        ref={tooltip => (this.tooltipComponent = tooltip)}
                        setTooltipRef={this.setTooltipRef}
                        setTooltipCrosshairRef={this.setTooltipCrosshairRef}
                        annotationComponent={Annotation}
                        adminUser={isAdmin()}
                        canEdit={canManage()}
                        currentUser={currentUser}
                        dashboardId={dashboardId}
                        eventsHovered={eventsHovered}
                        mode={mode}
                        onDeleteEvent={this.onDeleteEvent}
                        onHideTooltip={this.onClose}
                        showTooltip={showTooltip}
                        showForm={showForm}
                        tooltip={tooltip}
                        activeDashboardFilters={activeDashboardFilters}
                    />
                </div>
            </div>
        )
    }

    throttleFunctions = (): void => {
        this.redraw = _.throttle(this.redraw.bind(this), 300)
        this.updateD3Scale = _.throttle(this.updateD3Scale.bind(this), 300)
    }
}

/**
 * Gets date values.
 *
 * @param {Number} value unix timestamp
 * @param {String} mode string ["temporal", "numeric"]
 *
 * @returns { value: Number | Moment, date: String | void, time: String | void }
 */
function getDateValues(value, mode): { value: number | Moment; date: string | void; time: string | void } {
    let date
    let time

    if (mode === 'temporal') {
        value = DateService.getMoment(value)
        date = value.format(DateService.getDateShortYearFormat())
        time = value.format(DateService.getTimeFormat())
    } else {
        value = parseInt(value)
    }

    return { value, date, time }
}

function moveTooltip(e: MouseEvent | FakeMouseEvent): void {
    this.mouseCurrentX = typeof e.overrideX === 'number' ? e.overrideX : normalizedOffsetX(e, this.refs.svg)
    if (!this.d3.scale || ((this.state.showTooltip || this.state.showForm) && !e.togglingTooltip)) {
        // Skip animating the tooltip while the tooltip is open
        // (except when tooltip just opened and we have to reposition it once in that case)
        return
    }

    if (this.animationFrameId != null) window.cancelAnimationFrame(this.animationFrameId)

    this.animationFrameId = window.requestAnimationFrame(() => {
        this.animationFrameId = undefined
        const pos = this.mouseCurrentX
        const xValue = this.d3.scale.invert(pos)
        const { value, date, time } = getDateValues(xValue, this.props.timelineConfig.mode)

        if (value instanceof moment) if (this.props.timelineConfig.mode === 'temporal' && !value.unix()) return

        const eventsHovered = []

        this.refs.cursor.setAttribute('x', pos - CURSOR_WIDTH / 2)

        const numberOfNotes = this.d3.ranges.reduce((prev, cur) => {
            const isHovered = cur.begin <= pos && cur.end >= pos

            if (isHovered) {
                eventsHovered.push(cur.event)
                select(cur.element).style('fill-opacity', '0.95')

                return prev + 1
            }

            select(cur.element).style('fill-opacity', '0.7')

            return prev
        }, 0)

        this.setState({
            ...this.state,
            eventsHovered,
            tooltip: {
                value,
                date,
                time,
                numberOfNotes,
                pos
            }
        })

        // The tooltip is normally centered on the mouse position.
        // on the right and left borders we keep the tooltip at the max or min position and move the crosshair
        const delta = this.d3.dimensions.width - pos
        const TOOLTIP_WIDTH = this.state.showTooltip ? 345 : 140
        const CROSSHAIR_WIDTH = 16
        const TOOLTIP_WINDOW_BORDER = TIMELINE_MARGIN / 2
        if (delta + TOOLTIP_WINDOW_BORDER < TOOLTIP_WIDTH / 2) {
            // RIGHT BORDER
            this.tooltipRef.style.transform = `translate(${
                this.d3.dimensions.width - TOOLTIP_WIDTH + TOOLTIP_WINDOW_BORDER
            }px)`
            this.tooltipCrosshairRef.style.transform = `translate(${
                TOOLTIP_WIDTH - CROSSHAIR_WIDTH / 2 - delta - TOOLTIP_WINDOW_BORDER
            }px)`
        } else {
            if (pos < TOOLTIP_WIDTH / 2 - TOOLTIP_WINDOW_BORDER) {
                // LEFT BORDER
                this.tooltipRef.style.transform = `translate(${-TOOLTIP_WINDOW_BORDER}px)`
                this.tooltipCrosshairRef.style.transform = `translate(${pos + 1}px)`
            } else {
                // NORMAL POSITIONING
                this.tooltipRef.style.transform = `translate(${pos - TOOLTIP_WIDTH / 2}px)`
                this.tooltipCrosshairRef.style.transform = `translate(${TOOLTIP_WIDTH / 2 - CROSSHAIR_WIDTH / 2}px)`
            }
        }
    })
}

function mouseLeave(): void {
    window.requestAnimationFrame(() => {
        if (
            ((this.state.showTooltip && !this.state.eventsHovered.length) || !this.state.showTooltip) &&
            !this.state.showForm
        ) {
            // hides highcharts tooltips
            $(window).trigger('click')

            this.refs.cursor.style.fill = 'transparent'
            this.cursorShowing = false
            this.tooltipRef.style.opacity = 0
        }
    })
    pointService.currentPoint$.next({ x: null })
}

function normalizedOffsetX(e: MouseEvent, target): number {
    const rect = target.getBoundingClientRect()
    const offsetX = e.clientX - rect.left

    return offsetX
}
